import { useTranslations } from "next-intl";

import { Icon } from "@/components/icons/Icon";
import { collapseLabelText } from "@/components/tiptap/collapse/utils";
import { useTooltip } from "@/components/tooltip/Tooltip";
import { twMerge } from "@/utils/twMerge";

import type { ComponentProps, ReactNode } from "react";

type Props = {
  collapsed: boolean;
  nodeTypeName?: string;
  icon?: ReactNode;
};

export function CollapseToggle({
  collapsed,
  nodeTypeName = "block",
  className,
  onClick,
  onMouseDown,
  icon,
}: ComponentProps<"button"> & Props) {
  const t = useTranslations("tiptap");
  const label = collapseLabelText(collapsed, nodeTypeName, t);
  const { handlerProps, tooltip } = useTooltip({ title: label });

  return (
    <>
      <button
        type="button"
        aria-label={label}
        aria-expanded={!collapsed}
        tabIndex={-1}
        onClick={onClick}
        onMouseDown={onMouseDown}
        data-collapse-toggle
        className={twMerge(
          "relative flex items-center justify-center w-5 h-5 py-0.5 shrink-0 z-10 select-none cursor-pointer",
          "rounded-6 transition-colors text-sm tracking-tight hoverable-icon-main hover:bg-sidebar-button-hover-bg",
          collapsed
            ? "collapse-expand [--color-icon-main:var(--color-icon-active)]"
            : "[--color-icon-main:var(--color-line-number-text)]",
          className,
        )}
        {...handlerProps}
      >
        {icon ?? <Icon name={collapsed ? "Plus" : "Minus"} aria-hidden />}
      </button>
      {tooltip}
    </>
  );
}

export function CollapseSection({ collapsed, ...props }: ComponentProps<"button"> & Props) {
  return (
    <CollapseToggle
      {...props}
      collapsed={collapsed}
      icon={
        <Icon
          name="ChevronDown"
          aria-hidden
          className={twMerge("w-6", collapsed ? "-rotate-90" : "")}
        />
      }
    />
  );
}
